import { ActionList, Button, ButtonGroup, Card, Icon, Link, Popover } from "@shopify/polaris"
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon,  MenuHorizontalIcon, XIcon } from "@shopify/polaris-icons"
import classnames from "classnames"
import { useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import dashboard from "@/api/dashboard"
import { TMSMigration } from "@/api/dashboard/type"
import CardTitle from "@/component/TitleGroup/CardTitle"

import styles from "./index.module.scss"
import { MIGRATE_SETUP_GUIDE_TAB_CONTENTS } from "./tabs"

const MIGRATE_SETUP_GUIDE_LEARN_MORE_URL = "https://docs.parcelpanel.com/shopify/faq/migrate-from-trackingmore/"

interface MigrateSetupGuideProps {
  onDismiss?: () => void
  /** 由父组件（Dashboard）请求并下发，避免重复调用 getTmsMigration 接口 */
  initialTmsMigration?: TMSMigration | null
}

interface MigrateSetupGuideTab {
  id: string
  label: string
  isCompleted?: boolean
}

const MigrateSetupGuide = ({ onDismiss, initialTmsMigration }: MigrateSetupGuideProps) => {
  const { t } = useTranslation(["dashboard", "common"])

  // TMS 迁移数据（由父组件下发，无需重复请求）
  const [tmsMigration, setTmsMigration] = useState<TMSMigration | null>(initialTmsMigration ?? null)

  const MIGRATE_SETUP_GUIDE_TABS: MigrateSetupGuideTab[] = [
    {
      id: "sync-orders",
      label: t("dashboard:MigrateSetupGuideStepSyncOrders"),
      isCompleted: tmsMigration?.progresses[0]?.completed ?? false,
    },
    {
      id: "tracking-page",
      label: t("dashboard:MigrateSetupGuideStepTrackingPage"),
      isCompleted: tmsMigration?.progresses[1]?.completed ?? false,
    },
    {
      id: "core-settings",
      label: t("dashboard:MigrateSetupGuideStepCoreSettings"),
      isCompleted: tmsMigration?.progresses[2]?.completed ?? false,
    },
    {
      id: "good-to-know",
      label: t("dashboard:MigrateSetupGuideStepGoodToKnow"),
      isCompleted: tmsMigration?.progresses[3]?.completed ?? false,
    },
  ]

  // 是否展开（null 表示数据未加载完成，避免先展开再收起的闪烁）
  const [expand, setExpand] = useState<boolean | null>(null)

  // 当前步骤
  const [step, setStep] = useState(0)

  const [dismissPopoverShow, setDismissPopoverShow] = useState(false)
  const [isDismissLoading, setIsDismissLoading] = useState(false)

  // 核心设置勾选项提升到此处，避免切换 tab 后丢失用户修改并被 default 重置
  const [coreSettingsSelectedIds, setCoreSettingsSelectedIds] = useState<string[] | null>(null)

  // 仅在首次加载数据时自动选中未完成的 Tab，避免完成步骤后跳转
  const hasAutoSelectedStepRef = useRef(false)

  // 串行化 complete 请求，避免并发请求返回过期快照覆盖掉已完成的步骤
  const completeStepQueueRef = useRef<Promise<unknown>>(Promise.resolve())

  useEffect(() => {
    // 重置
    // let temp = 0
    // const timer = setInterval(() => {
    //   dashboard.tmsMigrationReopen({ step: temp })
    //   temp += 1
    //   if (temp > 3){
    //     clearInterval(timer)
    //   }
    // }, 1000)
  }, [])

  // 数据由父组件下发，仅在首次拿到数据时确定展开状态并自动选中未完成的 Tab
  useEffect(() => {
    if (!initialTmsMigration || hasAutoSelectedStepRef.current) {
      return
    }

    hasAutoSelectedStepRef.current = true
    setTmsMigration(initialTmsMigration)

    const { progresses } = initialTmsMigration
    const isAllCompleted = progresses.every(progress => progress.completed)

    setExpand(!isAllCompleted)

    const firstIncompleteIndex = progresses.findIndex(progress => !progress.completed)

    if (firstIncompleteIndex !== -1) {
      setStep(firstIncompleteIndex)
    }
  }, [initialTmsMigration])

  const migrateSetupGuideDescription = (
    <Trans ns="dashboard" i18nKey="MigrateSetupGuideDescription">
      <Link target="_blank" removeUnderline url={MIGRATE_SETUP_GUIDE_LEARN_MORE_URL} />
    </Trans>
  )

  const ActiveTabContent = MIGRATE_SETUP_GUIDE_TAB_CONTENTS[step]

  // 折叠状态尚未确定（数据未加载完成）时不渲染，避免先展开后折叠的抖动
  if (expand === null) {
    return null
  }

  const handleCompleteStep = (stepIndex: number, importSettings?: any): Promise<boolean> => {
    const task = completeStepQueueRef.current.then(async () => {
      try {
        const { data } = await dashboard.completeTmsMigrationStep({
          step: stepIndex,
          import_settings: importSettings || null,
        })

        if (data?.tms_migrations) {
          // complete 接口不返回 tracking_page，合并而非整体替换，避免覆盖丢失该字段
          setTmsMigration(prev => ({
            ...prev,
            ...data.tms_migrations,
            tracking_page: data.tms_migrations.tracking_page ?? prev?.tracking_page,
          }))

          const isAllCompleted = data.tms_migrations.progresses.every(progress => progress.completed)

          if (isAllCompleted) {
            // setExpand(false)
          }
        }

        return true
      } catch (error) {
        console.error("Failed to complete TMS migration step:", error)
        return false
      }
    })

    // 保持队列链不被 reject 中断
    completeStepQueueRef.current = task.catch(() => undefined)

    return task
  }

  const handleDismiss = async () => {
    if (isDismissLoading) {
      return
    }

    setIsDismissLoading(true)
    setDismissPopoverShow(false)
    try {
      const { status } = await dashboard.tmsMigrationDismiss()

      if (status === 204) {
        onDismiss?.()
      }
    } catch (error) {
      console.error("Failed to dismiss TMS migration guide:", error)
    } finally {
      setIsDismissLoading(false)
    }
  }

  return (
    <div className={"mb-4"}>
      <Card>
        <CardTitle
          title={t("dashboard:MigrateSetupGuideTitle")}
          description={<div className={"mt-2"}>{migrateSetupGuideDescription}</div>}
          titleRight={
            <div className={"flex gap-4"}>
              <Popover
                active={dismissPopoverShow}
                activator={
                  <Button
                    variant={"tertiary"}
                    icon={<Icon source={MenuHorizontalIcon} />}
                    disabled={isDismissLoading}
                    onClick={() => {
                      if (!isDismissLoading) {
                        setDismissPopoverShow(!dismissPopoverShow)
                      }
                    }}
                  />
                }
                onClose={() => {
                  if (!isDismissLoading) {
                    setDismissPopoverShow(false)
                  }
                }}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[{
                    content: t("dashboard:dismiss"),
                    /* @ts-ignore*/
                    icon: XIcon,
                    disabled: isDismissLoading,
                    onAction: handleDismiss,
                  }]}
                />
              </Popover>
              <div>
                <Button
                  variant={"tertiary"}
                  icon={<Icon source={expand ? ChevronUpIcon : ChevronDownIcon} />}
                  onClick={() => setExpand(prev => !prev)}
                />
              </div>
            </div>
          }
        />

        {
          expand && <div className={"mt-4"}>
            <ButtonGroup variant={"segmented"} fullWidth>
              {MIGRATE_SETUP_GUIDE_TABS.map((tab, index) => (
                <Button
                  key={tab.id}
                  variant={"secondary"}
                  size={"large"}
                  pressed={step === index}
                  onClick={() => setStep(index)}
                  children={
                    (
                      <div className={classnames("inline-flex items-center justify-center gap-1", styles.buttonWrapper)}>
                        <span>{tab.label}</span>
                        {tab.isCompleted ? (
                          <Icon tone={"success"} source={CheckCircleIcon} />
                        ) : null}
                      </div>
                    ) as unknown as string
                  }
                />
              ))}
            </ButtonGroup>
            <ActiveTabContent
              tmsMigration={tmsMigration}
              onCompleteStep={handleCompleteStep}
              isCompleted={MIGRATE_SETUP_GUIDE_TABS[step]?.isCompleted ?? false}
              coreSettingsSelectedIds={coreSettingsSelectedIds}
              onCoreSettingsSelectedIdsChange={setCoreSettingsSelectedIds}
            />
          </div>
        }
      </Card>
    </div>
  )
}

export default MigrateSetupGuide
